import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommonService } from '../../../api/services/common.service';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { ToastrService } from 'ngx-toastr';
import { TaskTableComponent } from '../task-table/task-table.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { AuthService } from '../../../api/services/auth.service';
import { getAccessValue } from '../../../helpers/rbac.helper';

const TASK_LIST_FORM_ID = FORM_ID.TASK_LIST;
const FRAMEWORK_FORM_ID = FORM_ID.FRAMEWORKS;

@Component({
  selector: 'app-framework-details',
  standalone: true,
  imports: [CommonModule, FormsModule, TaskTableComponent],
  templateUrl: './framework-details.component.html',
  styleUrl: './framework-details.component.css',
})
export class FrameworkDetailsComponent implements OnInit, OnChanges {
  @Input() framework: any;
  @Input() isWriteAccess: boolean = true;
  @Input() isLoading: boolean = false;
  @Output() back = new EventEmitter<void>();

  profileResponse: any;
  activeTab: string = 'tasks';
  Math = Math;
  tasks: any[] = [];
  isLoadingTasks: boolean = false;
  isSavingSelection: boolean = false;

  // Search & Filters
  searchQuery: string = '';
  selectedStatus: string = 'All Status';
  selectedPriority: string = 'All Priority';
  selectedCategory: string = 'All Category';

  // Metrics
  totalTasksCount: number = 0;
  auditedCount: number = 0;
  verifiedCount: number = 0;
  deployedCount: number = 0;
  notApplicableCount: number = 0;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;

  // Table selections
  selectedTasks: { [id: string]: boolean } = {};
  allTasksSelected: boolean = false;
  private searchSubject$ = new Subject<string>();

  constructor(
    private commonService: CommonService,
    private formsService: FormsServiceService,
    private toaster: ToastrService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {
    this.searchSubject$
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((search) => {
        this.searchQuery = search;
        this.currentPage = 1;
        this.loadTasks();
      });
  }

  ngOnInit(): void {
    this.getProfileDetails();
  }

  getProfileDetails(): void {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data?.data;
        const accessVal = getAccessValue(
          this.profileResponse,
          'compliance-governance',
          'framework-library',
        );
        if (accessVal === 'read_only' || accessVal === 'no_access') {
          this.isWriteAccess = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['framework'] && this.framework) {
      this.loadTasks();
    }
  }

  loadTasks(): void {
    if (!this.framework || !this.framework.shortName) {
      this.tasks = [];
      this.resetMetrics();
      return;
    }

    this.isLoadingTasks = true;
    const options: any = {
      'mappedStandards.standardName': this.framework.shortName,
      limit: 1000,
    };

    if (this.searchQuery) {
      options.query = this.searchQuery;
    }

    this.commonService
      .loadListWithOptions(TASK_LIST_FORM_ID, options)
      .subscribe({
        next: (res) => {
          this.tasks = res?.data?.records ?? res?.data ?? [];
          this.calculateMetrics(res);
          this.isLoadingTasks = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toaster.error(
            'Failed to load tasks for this framework',
            'Error',
            {
              messageClass: 'addMessage',
              titleClass: 'addCss',
            },
          );
          this.tasks = [];
          this.resetMetrics();
          this.isLoadingTasks = false;
          this.cdr.detectChanges();
        },
      });
  }

  resetMetrics(): void {
    this.totalTasksCount = 0;
    this.auditedCount = 0;
    this.verifiedCount = 0;
    this.deployedCount = 0;
    this.notApplicableCount = 0;
  }

  calculateMetrics(res?: any): void {
    this.totalTasksCount = this.tasks.length;
    if (this.totalTasksCount === 0) {
      this.resetMetrics();
      return;
    }

    this.auditedCount = this.tasks.filter(
      (t) => t.status?.toLowerCase() === 'audited',
    ).length;
    this.verifiedCount = this.tasks.filter(
      (t) => t.status?.toLowerCase() === 'verified',
    ).length;
    this.deployedCount = this.tasks.filter(
      (t) => t.status?.toLowerCase() === 'deployed',
    ).length;
    this.notApplicableCount = this.tasks.filter(
      (t) => t.status?.toLowerCase() === 'not applicable',
    ).length;
  }

  // Get dynamic unique categories from tasks list
  get uniqueCategories(): string[] {
    const cats = this.tasks.map((t) => t.category).filter(Boolean);
    return Array.from(new Set(cats));
  }

  // Filtering Logic
  get filteredTasks(): any[] {
    return this.tasks.filter((task) => {
      // Status match
      const matchesStatus =
        this.selectedStatus === 'All Status'
          ? true
          : (task.status || '').toLowerCase() ===
            this.selectedStatus.toLowerCase();

      // Priority match
      const matchesPriority =
        this.selectedPriority === 'All Priority'
          ? true
          : (task.priority || '').toLowerCase() ===
            this.selectedPriority.toLowerCase();

      // Category match
      const matchesCategory =
        this.selectedCategory === 'All Category'
          ? true
          : (task.category || '').toLowerCase() ===
            this.selectedCategory.toLowerCase();

      return matchesStatus && matchesPriority && matchesCategory;
    });
  }

  // Pagination Logic
  get paginatedTasks(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredTasks.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredTasks.length / this.pageSize) || 1;
  }

  get totalFilteredCount(): number {
    return this.filteredTasks.length;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Row Selection logic
  toggleAllTasks(): void {
    this.allTasksSelected = !this.allTasksSelected;
    this.paginatedTasks.forEach((task) => {
      this.selectedTasks[task._id] = this.allTasksSelected;
    });
  }

  toggleTaskSelection(id: string): void {
    this.selectedTasks[id] = !this.selectedTasks[id];
    this.allTasksSelected = this.paginatedTasks.every(
      (task) => this.selectedTasks[task._id],
    );
  }

  showConfirmModal: boolean = false;
  confirmTitle: string = '';
  confirmMessage: string = '';
  pendingSelectionState: boolean = false;

  toggleFrameworkSelection(isSelected: boolean): void {
    if (!this.isWriteAccess) return;
    this.pendingSelectionState = isSelected;
    this.confirmTitle = isSelected
      ? 'Confirm Selection'
      : 'Confirm Deselection';
    if (isSelected && this.notApplicableCount > 0) {
      const taskPlural = this.notApplicableCount > 1 ? 'tasks' : 'task';
      this.confirmMessage = `Selecting this framework will change the status of ${this.notApplicableCount} <span class="font-semibold text-gray-500 dark:text-gray-400">Not Applicable</span> ${taskPlural} to <span class="font-semibold text-warning">To Do</span>. Do you really want to select the ${this.framework?.shortName || 'framework'}?`;
    } else {
      this.confirmMessage = isSelected
        ? `Do you really want to select the ${this.framework?.shortName || 'framework'}?`
        : `Do you really want to deselect the ${this.framework?.shortName || 'framework'}?`;
    }
    this.showConfirmModal = true;
    this.cdr.detectChanges();
    const modal = document.getElementById('confirm_modal');
    if (modal) modal.classList.add('show');
  }

  cancelSelectionToggle(): void {
    const modal = document.getElementById('confirm_modal');
    if (modal) modal.classList.remove('show');
    this.showConfirmModal = false;
    this.cdr.detectChanges();
  }

  confirmSelectionToggle(): void {
    const modal = document.getElementById('confirm_modal');
    if (modal) modal.classList.remove('show');
    this.showConfirmModal = false;
    if (this.isSavingSelection) return;

    this.isSavingSelection = true;
    const payload = {
      formId: FRAMEWORK_FORM_ID,
      recordId: this.framework._id,
      data: {
        isSelected: this.pendingSelectionState,
        isRecommended: this.pendingSelectionState
          ? false
          : !!this.framework?.isRecommendedFramework,
        isOptional: this.pendingSelectionState
          ? false
          : !!this.framework?.isOptionalFramework,
        isMandatory: this.pendingSelectionState
          ? false
          : !!this.framework?.isMandatoryFramework,
        isRecommendedFramework: this.pendingSelectionState
          ? !!this.framework?.isRecommended
          : false,
        isOptionalFramework: this.pendingSelectionState
          ? !!this.framework?.isOptional
          : false,
        isMandatoryFramework: this.pendingSelectionState
          ? !!this.framework?.isMandatory
          : false,
      },
    };

    this.formsService
      .submitFormData({ url: '', method: '' }, payload)
      .subscribe({
        next: (res) => {
          this.framework.isSelected = this.pendingSelectionState;
          this.isSavingSelection = false;
          if (this.pendingSelectionState) {
            this.toaster.success(
              'Framework added to selected library',
              'Success',
              {
                messageClass: 'addMessage',
                titleClass: 'addCss',
              },
            );
          } else {
            this.toaster.success(
              'Framework deselected successfully',
              'Success',
              {
                messageClass: 'addMessage',
                titleClass: 'addCss',
              },
            );
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toaster.error('Failed to update framework selection', 'Error', {
            messageClass: 'addMessage',
            titleClass: 'addCss',
          });
          this.isSavingSelection = false;
          this.cdr.detectChanges();
        },
      });
  }

  onFlagError(item: any): void {
    item._flagError = true;
  }

  selectedIds: any[] = [];

  get taskTableData(): any {
    return {
      records: this.paginatedTasks,
      totalCount: this.totalFilteredCount,
      page: this.currentPage,
      limit: this.pageSize,
    };
  }

  handleTableLoadData(event: any): void {
    if (event && event.options) {
      if (event.options.page) {
        this.currentPage = event.options.page;
      }
      if (event.options.limit) {
        this.pageSize = event.options.limit;
      }
      if (event.options.sort) {
        const sortField = event.options.sort.replace('-', '');
        const sortDir = event.options.sort.startsWith('-') ? 'desc' : 'asc';
        this.tasks.sort((a, b) => {
          let valA = a[sortField] || '';
          let valB = b[sortField] || '';
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          if (valA < valB) return sortDir === 'asc' ? -1 : 1;
          if (valA > valB) return sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }
      this.cdr.detectChanges();
    }
  }

  handleSelection(selected: any[]): void {
    this.selectedIds = selected;
    this.cdr.detectChanges();
  }

  onSearchInput(value: string): void {
    this.searchSubject$.next(value);
  }

  clearSearch(inputElement: HTMLInputElement): void {
    inputElement.value = '';
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadTasks();
  }

  readonly defaultIconUrl: string = 'assets/maps/assets/flags/global.png';

  onIconError(framework: any, event?: Event): void {
    if (!framework) return;
    framework._iconError = true;
    if (event && event.target) {
      (event.target as HTMLImageElement).src = this.defaultIconUrl;
    }
    this.cdr.detectChanges();
  }

  getFrameworkIconUrl(framework: any): string {
    if (!framework) {
      return this.defaultIconUrl;
    }
    if (framework._iconError) {
      return this.defaultIconUrl;
    }

    const iconUrl = (framework.iconUrl || '').trim();
    const lowerIcon = iconUrl.toLowerCase();
    const name = (framework.shortName || '').toLowerCase();
    const fullName = (framework.fullName || '').toLowerCase();
    const source = (framework.source || '').toLowerCase();
    const header = (framework.mappingColumnHeader || '').toLowerCase();
    const text = `${lowerIcon} ${name} ${fullName} ${source} ${header}`;

    if (text.includes('colombia.png') || text.includes('colombia')) {
      return 'assets/maps/assets/flags/colombia.png';
    }
    if (text.includes('bermuda.png') || text.includes('bermuda')) {
      return 'assets/maps/assets/flags/Bermuda.png';
    }
    if (text.includes('bahamas.png') || text.includes('bahamas')) {
      return 'assets/maps/assets/flags/bahamas.png';
    }
    if (text.includes('chile.png') || text.includes('chile')) {
      return 'assets/maps/assets/flags/chile.png';
    }
    if (
      text.includes('costa_rica.png') ||
      text.includes('costa-rica.png') ||
      text.includes('costa rica')
    ) {
      return 'assets/maps/assets/flags/costa-rica.png';
    }
    if (text.includes('peru.png') || text.includes('peru')) {
      return 'assets/maps/assets/flags/peru.png';
    }
    if (text.includes('uruguay.png') || text.includes('uruguay')) {
      return 'assets/maps/assets/flags/uruguay.png';
    }

    if (iconUrl) {
      return iconUrl;
    }

    return this.defaultIconUrl;
  }
}
